import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';

const emptyForm = () => ({
  nombre: '', apellido: '', dni: '', cuil: '', email: '',
  fecha_nacimiento: '', fecha_primera_consulta: '',
  estado: 'activo',
  telefonos: [{ tipo: 'Celular', numero: '' }],
  domicilios: [{ tipo: 'Real', direccion: '' }],
});

export default function ClienteModal({ open, onClose, onSaved, editing = null }) {
  const [form, setForm] = useState(emptyForm());
  const [guardando, setGuardando] = useState(false);
  const [toast, setToast] = useState(null);

  const editingId = editing?.id || null;
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        nombre: editing.nombre || '', apellido: editing.apellido || '',
        dni: editing.dni || '', cuil: editing.cuil || '', email: editing.email || '',
        fecha_nacimiento: editing.fecha_nacimiento || '',
        fecha_primera_consulta: editing.fecha_primera_consulta || '',
        estado: editing.estado || 'activo',
        telefonos: editing.clientes_telefonos?.length ? editing.clientes_telefonos : [{ tipo: 'Celular', numero: '' }],
        domicilios: editing.clientes_domicilios?.length ? editing.clientes_domicilios : [{ tipo: 'Real', direccion: '' }],
      });
    } else {
      setForm(emptyForm());
    }
  }, [open, editing]);

  function addTelefono() { setForm(f => ({ ...f, telefonos: [...f.telefonos, { tipo: 'Celular', numero: '' }] })); }
  function removeTelefono(i) { setForm(f => ({ ...f, telefonos: f.telefonos.filter((_, idx) => idx !== i) })); }
  function updateTelefono(i, field, value) {
    setForm(f => { const tels = [...f.telefonos]; tels[i] = { ...tels[i], [field]: value }; return { ...f, telefonos: tels }; });
  }
  function addDomicilio() { setForm(f => ({ ...f, domicilios: [...f.domicilios, { tipo: 'Real', direccion: '' }] })); }
  function removeDomicilio(i) { setForm(f => ({ ...f, domicilios: f.domicilios.filter((_, idx) => idx !== i) })); }
  function updateDomicilio(i, field, value) {
    setForm(f => { const doms = [...f.domicilios]; doms[i] = { ...doms[i], [field]: value }; return { ...f, domicilios: doms }; });
  }

  async function saveCliente() {
    if (guardando) return;
    if (!form.nombre.trim()) { showToast('El nombre es obligatorio'); return; }
    setGuardando(true);
    const clienteData = {
      nombre: form.nombre, apellido: form.apellido, dni: form.dni,
      cuil: form.cuil, email: form.email,
      fecha_nacimiento: form.fecha_nacimiento || null,
      fecha_primera_consulta: form.fecha_primera_consulta || null,
      estado: form.estado,
    };
    let clienteId = editingId;
    if (editingId) {
      const { error } = await supabase.from('clientes').update(clienteData).eq('id', editingId);
      if (error) { setGuardando(false); showToast('Error al guardar'); return; }
      await supabase.from('clientes_telefonos').delete().eq('cliente_id', editingId);
      await supabase.from('clientes_domicilios').delete().eq('cliente_id', editingId);
    } else {
      const result = await supabase.from('clientes').insert(clienteData).select('id').single();
      if (!result.data || result.error) { setGuardando(false); showToast('Error al guardar'); return; }
      clienteId = result.data.id;
    }
    const tels = form.telefonos.filter(t => t.numero.trim());
    if (tels.length) await supabase.from('clientes_telefonos').insert(tels.map(t => ({ tipo: t.tipo, numero: t.numero, cliente_id: clienteId })));
    const doms = form.domicilios.filter(d => d.direccion.trim());
    if (doms.length) await supabase.from('clientes_domicilios').insert(doms.map(d => ({ tipo: d.tipo, direccion: d.direccion, cliente_id: clienteId })));
    setGuardando(false);
    if (!editingId) setForm(emptyForm());
    showToast(editingId ? 'Cliente actualizado ✓' : 'Cliente registrado ✓');
    onSaved?.();
    onClose();
  }

  function handleClose() { if (!editingId) setForm(emptyForm()); onClose(); }

  if (!open) return null;

  return (
    <>
      <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && handleClose()}>
        <div className="modal" style={{ width: 620 }}>
          <div className="modal-header" style={{ padding: '13px 20px' }}>
            <div className="modal-title">{editingId ? 'Editar cliente' : 'Nuevo cliente'}</div>
            <button className="modal-close" onClick={handleClose}>✕</button>
          </div>
          <div className="modal-body" style={{ padding: '10px 20px', maxHeight: '80vh', overflowY: 'auto' }}>
            <div className="form-grid" style={{ gap: 8 }}>
              <div className="form-group" style={{ gap: 3 }}>
                <label className="form-label">Nombre *</label>
                <input className="form-input" autoFocus value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} placeholder="Nombre" />
              </div>
              <div className="form-group" style={{ gap: 3 }}>
                <label className="form-label">Apellido</label>
                <input className="form-input" value={form.apellido} onChange={e => setForm(f => ({ ...f, apellido: e.target.value }))} placeholder="Apellido" />
              </div>
              <div className="form-group" style={{ gap: 3 }}>
                <label className="form-label">DNI</label>
                <input className="form-input" value={form.dni} onChange={e => setForm(f => ({ ...f, dni: e.target.value }))} placeholder="DNI" />
              </div>
              <div className="form-group" style={{ gap: 3 }}>
                <label className="form-label">CUIL / CUIT</label>
                <input className="form-input" value={form.cuil} onChange={e => setForm(f => ({ ...f, cuil: e.target.value }))} placeholder="CUIL / CUIT" />
              </div>
              <div className="form-group" style={{ gap: 3 }}>
                <label className="form-label">Email</label>
                <input className="form-input" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="email@ejemplo.com" />
              </div>
              <div className="form-group" style={{ gap: 3 }}>
                <label className="form-label">Estado</label>
                <select className="form-select" value={form.estado} onChange={e => setForm(f => ({ ...f, estado: e.target.value }))}>
                  <option value="activo">Activo</option>
                  <option value="inactivo">Inactivo</option>
                  <option value="potencial">Potencial</option>
                </select>
              </div>
              <div className="form-group" style={{ gap: 3 }}>
                <label className="form-label">Fecha de nacimiento</label>
                <input className="form-input" type="date" value={form.fecha_nacimiento} onChange={e => setForm(f => ({ ...f, fecha_nacimiento: e.target.value }))} />
              </div>
              <div className="form-group" style={{ gap: 3 }}>
                <label className="form-label">Primera consulta <span style={{ fontWeight: 400, color: 'var(--text4)' }}>(opcional)</span></label>
                <input className="form-input" type="date" value={form.fecha_primera_consulta} onChange={e => setForm(f => ({ ...f, fecha_primera_consulta: e.target.value }))} />
              </div>
              <div className="form-group form-full" style={{ gap: 3 }}>
                <label className="form-label">Teléfonos</label>
                {form.telefonos.map((t, i) => (
                  <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
                    <select className="form-select" style={{ width: 120 }} value={t.tipo} onChange={e => updateTelefono(i, 'tipo', e.target.value)}>
                      <option>Celular</option><option>Fijo</option><option>Laboral</option><option>Otro</option>
                    </select>
                    <input className="form-input" style={{ flex: 1 }} value={t.numero} onChange={e => updateTelefono(i, 'numero', e.target.value)} placeholder="Número" />
                    {form.telefonos.length > 1 && <button className="btn btn-danger btn-sm" onClick={() => removeTelefono(i)}>✕</button>}
                  </div>
                ))}
                <button className="btn btn-ghost btn-sm" onClick={addTelefono}>+ Agregar teléfono</button>
              </div>
              <div className="form-group form-full" style={{ gap: 3 }}>
                <label className="form-label">Domicilios</label>
                {form.domicilios.map((d, i) => (
                  <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
                    <select className="form-select" style={{ width: 120 }} value={d.tipo} onChange={e => updateDomicilio(i, 'tipo', e.target.value)}>
                      <option>Real</option><option>Legal</option><option>Laboral</option><option>Otro</option>
                    </select>
                    <input className="form-input" style={{ flex: 1 }} value={d.direccion} onChange={e => updateDomicilio(i, 'direccion', e.target.value)} placeholder="Dirección" />
                    {form.domicilios.length > 1 && <button className="btn btn-danger btn-sm" onClick={() => removeDomicilio(i)}>✕</button>}
                  </div>
                ))}
                <button className="btn btn-ghost btn-sm" onClick={addDomicilio}>+ Agregar domicilio</button>
              </div>
            </div>
          </div>
          <div className="modal-footer" style={{ padding: '10px 20px' }}>
            <button className="btn btn-ghost" onClick={handleClose}>Cancelar</button>
            <button className="btn btn-primary" onClick={saveCliente} disabled={guardando}>{guardando ? 'Guardando...' : editingId ? 'Guardar cambios' : 'Guardar cliente'}</button>
          </div>
        </div>
      </div>
      {toast && <div className="toast" style={{ zIndex: 1200 }}>✓ {toast}</div>}
    </>
  );
}
